/**
 * App.tsx
 *
 * Root application component. Renders the theme provider, global styles,
 * header layout, and routes. Registers the authenticated user with the
 * SignalR UserRelayHub when connected.
 */

// ============= Imports =============

import React, { useEffect } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { ThemeProvider } from '@tw/tw-component-library';

import { addClientToGroup } from './apis/core/user/post/addClientToUsers';
import { HeaderLayout } from './components/layout/HeaderLayout';
import { AppRoutes } from './components/routing/AppRoutes';
import { UseAuth } from './contexts/AuthContext';
import { useDebug } from './contexts/DebugContext';
import { useSignalR } from '@/contexts/signalR';
import { useWorkspace } from './contexts/WorkspaceContext';
import GlobalStyles from './styles/GlobalStyles';
import { useAppTheme } from './hooks/useAppTheme';

// ============= Constants =============

/** SignalR hub name for user-related real-time messaging. */
const USER_RELAY_HUB_NAME = 'userRelayHub';

/** Hub names to inspect for debug logging (development only). */
const DEBUG_HUB_NAMES = [
  'userRelayHub',
  'workspaceRelayHub',
  'askRelayHub',
] as const;

/**
 * Default Litium design tokens use the library's `light` theme. Winston Taylor
 * swaps in its own token set. Mapped from the brand axis at render time.
 */
const LITIUM_THEME_MODE = 'light';
const WINSTON_TAYLOR_THEME_MODE = 'winstonTaylor';

// ============= Component =============

/**
 * Root App component.
 *
 * - Wraps the app in ThemeProvider and GlobalStyles.
 * - Renders HeaderLayout with auth props and AppRoutes.
 * - When authenticated, WorkspaceContext has finished loading, and UserRelayHub
 *   is connected, adds the client to the user group so the server can target this
 *   user for real-time messages. Deferring until workspace data is ready ensures
 *   USER_ONLINE handlers can resolve display names from workspace member data.
 *
 * @returns The root React element.
 */
const App: React.FC = () => {
  const {
    isAuthenticated,
    isInitializing,
    activeAccount,
    login,
    logout,
    getAccessToken,
    currentUser,
  } = UseAuth();
  const { invokeHub, connectionStates, inspectHandlers } = useSignalR();
  const { isLoading: isWorkspaceContextLoading } = useWorkspace();
  const { debug: debugState, canShowDebug } = useDebug();
  const { isWinstonTaylor } = useAppTheme();
  const twThemeMode = isWinstonTaylor
    ? WINSTON_TAYLOR_THEME_MODE
    : LITIUM_THEME_MODE;

  // Add client to user group when authenticated, workspace data is ready, and
  // UserRelayHub is connected. Deferring until workspace context has loaded
  // ensures USER_ONLINE (and other) handlers can resolve display names from
  // workspace member data; joining earlier would fire events before data exists.
  useEffect(() => {
    if (!isAuthenticated || !currentUser?.userId) {
      return;
    }

    if (isWorkspaceContextLoading) {
      return;
    }

    const connectionState = connectionStates.get(USER_RELAY_HUB_NAME);
    const isUserHubConnected = connectionState === HubConnectionState.Connected;

    // Log hub handlers when debug option is on (e.g. from Debug panel).
    if (canShowDebug && debugState.logHubHandlers && inspectHandlers) {
      DEBUG_HUB_NAMES.forEach((hubName) => {
        const summary = inspectHandlers(hubName);
        console.log(`🔎 ${hubName} handlers summary`, summary);
      });
    }

    if (!isUserHubConnected) {
      return;
    }

    const joinUserGroup = async () => {
      try {
        await addClientToGroup({
          invokeHub,
          workspaceId: (currentUser.workspaceId as unknown as string) || '',
          userId: currentUser.userId,
        });
        if (import.meta.env.DEV) {
          console.log(
            `✅ Client added to user group [${currentUser.userId}] in UserRelayHub`,
          );
        }
      } catch (error) {
        console.error(
          `🔴 Error adding client to user group [${currentUser.userId}]:`,
          error,
        );
      }
    };

    void joinUserGroup();
  }, [
    isAuthenticated,
    currentUser,
    currentUser?.userId,
    invokeHub,
    connectionStates,
    inspectHandlers,
    isWorkspaceContextLoading,
    canShowDebug,
    debugState.logHubHandlers,
  ]);

  return (
    <ThemeProvider themeMode={twThemeMode}>
      <GlobalStyles />
      <HeaderLayout
        isAuthenticated={isAuthenticated}
        activeAccount={activeAccount}
        login={login}
        logout={logout}
        getAccessToken={getAccessToken}
      >
        <AppRoutes
          isAuthenticated={isAuthenticated}
          isInitializing={isInitializing}
        />
      </HeaderLayout>
    </ThemeProvider>
  );
};

// ============= Exports =============

export default App;
